<template>
  <b-modal
    ref="modal"
    id="huddle-metrics-explainer"
    size="lg"
    body-class="bg-white"
    hide-footer
    scrollable
    @shown="onShown"
  >
    <template #modal-title>
      What do these numbers mean?
    </template>

    <div class="metrics-explainer">
      <p class="me-intro">
        Every production figure on this page is built the same way, and it all follows the date
        picker at the top — pick another day and every number below reads as of that day.
      </p>

      <div class="me-formula-banner">
        <span class="me-formula-banner-label">The one formula behind all of it</span>
        Net Production = Gross Production − Insurance write-offs + Adjustments
      </div>

      <div class="me-callout">
        <i class="la la-exclamation-circle" />
        <div>
          <strong>The claim has to be generated — not sent.</strong>
          Insurance write-offs are what turn gross production into net production, and they do
          not exist in Open Dental until a claim exists. Open Dental's recommended workflow is to
          generate the claim as part of checkout, so the patient is charged the right amount, then
          batch and send the day's claims with one click at the end of the day. If your team
          generates claims at end of day instead, today's completed production will read low until
          they do, and will look correct the next morning.
        </div>
      </div>

      <section
        v-for="entry in entries"
        :key="entry.key"
        :id="`metric-${entry.key}`"
        class="me-section"
        :class="{ 'me-section-focused': entry.key === focusKey }"
      >
        <h6 class="me-section-title">{{ entry.label }}</h6>
        <p class="me-section-short">{{ entry.short }}</p>

        <p class="me-section-formula">
          <span class="me-section-formula-label">How it's calculated</span>
          {{ entry.formula }}
        </p>

        <p v-if="entry.gotcha" class="me-section-gotcha">{{ entry.gotcha }}</p>
      </section>

      <p class="me-footer">
        Still not matching what you expect?
        <a :href="docsBase" target="_blank" rel="noopener">
          Read the full article in the Help Center
          <i class="la la-external-link" />
        </a>
        — it walks through how these line up with Open Dental's own production reports.
      </p>
    </div>
  </b-modal>
</template>

<script>
/* eslint-disable */

import { entries as glossaryEntries, DOCS_BASE } from '../huddleMetricGlossary';

/**
 * The full "what do these numbers mean?" breakdown for Today's Huddle.
 *
 * Renders straight from huddleMetricGlossary.js — the same source the inline info icons use —
 * so the short and long explanations can never disagree.
 */
export default {
  name: 'MetricsExplainerModal',

  props: {
    // Which glossary section to scroll to on open. Null opens at the top.
    focusKey: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      entries: glossaryEntries,
      docsBase: DOCS_BASE,
    };
  },

  methods: {
    onShown() {
      // The modal DOM persists between opens, so its scroll position does too — reset it,
      // otherwise the header button reopens wherever the last info icon scrolled to.
      this.$nextTick(() => {
        if (this.focusKey) this.scrollToFocused();
        else this.resetScroll();
      });
    },

    resetScroll() {
      const modalEl = this.$refs.modal && this.$refs.modal.$el;
      const body = modalEl && modalEl.querySelector('.modal-body');
      if (body) body.scrollTop = 0;
    },

    /**
     * Scroll the modal body — NOT the page — to the requested section. Measured from bounding
     * rects rather than offsetTop because .modal-body is not a positioned ancestor, so the
     * sections' offsetParent is not the element doing the scrolling.
     */
    scrollToFocused() {
      const modalEl = this.$refs.modal && this.$refs.modal.$el;
      if (!modalEl) return;

      const body = modalEl.querySelector('.modal-body');
      const target = modalEl.querySelector(`#metric-${this.focusKey}`);
      if (!body || !target) return;

      const delta = target.getBoundingClientRect().top - body.getBoundingClientRect().top;
      body.scrollTop += delta - 8;
    },
  },
};
</script>

<style lang="scss" scoped>
.metrics-explainer {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, sans-serif;
  color: #334155;
  font-size: 13.5px;
  line-height: 1.6;
}

.me-intro {
  margin-bottom: 16px;
  color: #64748b;
}

.me-formula-banner {
  padding: 14px 16px;
  margin-bottom: 16px;
  background: #f1f5f9;
  border-left: 4px solid #3abf94;
  border-radius: 0 8px 8px 0;
  font-size: 14.5px;
  font-weight: 600;
  color: #0f172a;
}

.me-formula-banner-label {
  display: block;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 4px;
}

.me-callout {
  display: flex;
  gap: 10px;
  padding: 14px 16px;
  margin-bottom: 24px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  color: #b45309;

  i {
    flex-shrink: 0;
    font-size: 18px;
    line-height: 1.4;
  }

  strong {
    display: block;
    color: #92400e;
  }
}

.me-section {
  padding: 16px 0;
  border-top: 1px solid #e2e8f0;
  scroll-margin-top: 8px;
}

.me-section-focused {
  padding-left: 12px;
  margin-left: -12px;
  border-left: 3px solid #3abf94;
  background: #f0fdf9;
  border-radius: 0 8px 8px 0;
}

.me-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 6px;
}

.me-section-short {
  margin-bottom: 10px;
}

.me-section-formula {
  padding: 8px 12px;
  margin-bottom: 10px;
  background: #f8fafc;
  border-radius: 6px;
  color: #0f172a;
}

.me-section-formula-label {
  display: block;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 2px;
}

.me-section-gotcha {
  padding: 10px 12px;
  margin-bottom: 0;
  background: #fffbeb;
  border-left: 3px solid #f59e0b;
  border-radius: 0 6px 6px 0;
  color: #b45309;
}

.me-footer {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  font-size: 12.5px;
  color: #64748b;

  a {
    color: #3abf94;
    font-weight: 600;
  }
}
</style>
